import { useState } from "react";
import { Link } from "react-router-dom";
import AnnouncementBar from "./AnnouncementBar";
import { useCart } from "@/context/CartContext";

const navItems = [
  { label: "Início", href: "/" },
  { label: "Acessórios", href: "/", children: true },
  { label: "Novidades", href: "/" },
  { label: "Promoções", href: "/" },
  { label: "Sobre nós", href: "/" },
  { label: "Contato", href: "/contato" },
];

const accessoriesMenu = [
  "Brincos",
  "Pulseiras",
  "Anéis",
  "Religiosos",
  "Óculos",
  "Bolsas",
  "Carteiras",
  "Conjuntos",
];

function Logo() {
  return (
    <Link to="/" aria-label="Rica Tanto — início" className="brand-logo flex flex-col items-center gap-0.5 group">
      <img src={`${import.meta.env.BASE_URL}rica-tanto-logo-transparent.png`} alt="Rica Tanto" />
    </Link>
  );
}

export default function Header() {
  const { totalItems, openCart } = useCart();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  return (
    <header className="sticky top-0 z-40 w-full">
      <AnnouncementBar />

      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-none items-center gap-4 px-4 py-3 md:gap-8 md:px-6 md:py-4">
          <button
            type="button"
            aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="mobile-menu-toggle flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 md:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>

          <Logo />

          <nav className="desktop-navigation hidden flex-1 items-center justify-center gap-1 md:flex">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-700"
                >
                  {item.label}
                  {item.children && <i className="ri-arrow-down-s-line text-base"></i>}
                </Link>
                {item.children && activeDropdown === item.label && (
                  <div className="absolute left-0 top-full z-50 w-52 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2 shadow-lg">
                    {accessoriesMenu.map((entry) => (
                      <Link
                        key={entry}
                        to="/"
                        className="block px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                      >
                        {entry}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <form
            className="desktop-search hidden w-[260px] lg:block"
            onSubmit={(event) => event.preventDefault()}
            role="search"
          >
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="text"
                name="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar produtos..."
                className="h-10 w-full rounded-full border border-background-200 bg-background-100/70 pl-10 pr-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
              />
            </div>
          </form>

          <div className="ml-auto flex items-center gap-1 md:ml-0 md:gap-2">
            <div
              className="relative hidden md:block"
              onMouseEnter={() => setActiveDropdown("conta")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                type="button"
                aria-label="Minha conta"
                className="flex h-9 w-9 items-center justify-center rounded-full text-foreground-800 transition-colors hover:bg-background-100 hover:text-primary-700"
              >
                <i className="ri-user-line text-xl"></i>
              </button>
              {activeDropdown === "conta" && (
                <div className="absolute right-0 top-full z-50 w-48 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2 shadow-lg">
                  <Link to="/" className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100">
                    Entrar
                  </Link>
                  <Link to="/" className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100">
                    Criar conta
                  </Link>
                  <Link to="/" className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100">
                    Meus pedidos
                  </Link>
                </div>
              )}
            </div>

            <button
              type="button"
              aria-label="Abrir carrinho"
              onClick={openCart}
              className="relative flex h-9 w-9 items-center justify-center rounded-full text-foreground-800 transition-colors hover:bg-background-100 hover:text-primary-700"
            >
              <i className="ri-shopping-cart-2-line text-xl"></i>
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-background-50">
                {totalItems}
              </span>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div id="mobile-navigation" className="mobile-navigation border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
            <form onSubmit={(event) => event.preventDefault()} role="search" className="mb-3">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base"></i>
                </span>
                <input
                  type="text"
                  name="search_mobile"
                  placeholder="Buscar produtos..."
                  className="h-10 w-full rounded-full border border-background-200 bg-background-100/70 pl-10 pr-4 text-sm outline-none focus:border-primary-400"
                />
              </div>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="border-b border-background-100 py-2.5 text-sm font-medium text-foreground-800"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}



